import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BulkApprovalPanel = ({ profiles, selectedProfiles, onBulkApprove, onBulkReject, onSelectAll }) => {
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleBulkApprove = async () => {
    if (selectedProfiles?.length === 0) {
      alert('Please select profiles to approve');
      return;
    }
    if (!confirm(`Are you sure you want to approve ${selectedProfiles?.length} profiles?`)) {
      return;
    }
    setIsProcessing(true);
    try {
      await onBulkApprove();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkReject = async () => {
    if (!bulkRejectReason?.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    setIsProcessing(true);
    try {
      await onBulkReject(bulkRejectReason);
      setShowBulkRejectModal(false);
      setBulkRejectReason('');
    } finally {
      setIsProcessing(false);
    }
  };

  const selectedProfilesData = profiles?.filter(p => selectedProfiles?.includes(p?.id));

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-card rounded-2xl p-6 border border-border">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Icon name="Layers" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground">Bulk Approval Actions</h2>
              <p className="text-sm text-muted-foreground">Process multiple profiles simultaneously</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-background rounded-xl p-4">
              <p className="text-sm text-muted-foreground mb-1">Total Profiles</p>
              <p className="text-3xl font-bold text-foreground">{profiles?.length}</p>
            </div>
            <div className="bg-primary/10 rounded-xl p-4">
              <p className="text-sm text-muted-foreground mb-1">Selected</p>
              <p className="text-3xl font-bold text-primary">{selectedProfiles?.length}</p>
            </div>
            <div className="bg-warning/10 rounded-xl p-4">
              <p className="text-sm text-muted-foreground mb-1">Remaining</p>
              <p className="text-3xl font-bold text-warning">{profiles?.length - selectedProfiles?.length}</p>
            </div>
          </div>
        </div>

        {/* Selection Controls */}
        <div className="bg-card rounded-2xl p-6 border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4">Selection Controls</h3>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={onSelectAll}
              variant="outline"
              iconName="CheckSquare"
            >
              {selectedProfiles?.length === profiles?.length ? 'Deselect All' : 'Select All'}
            </Button>
            <Button
              onClick={() => {
                // Select only candidates
                const candidateIds = profiles?.filter(p => p?.role === 'candidate')?.map(p => p?.id);
                onSelectAll(candidateIds);
              }}
              variant="outline"
              iconName="Users"
            >
              Select All Candidates
            </Button>
            <Button
              onClick={() => {
                // Select only employers
                const employerIds = profiles?.filter(p => p?.role === 'employer')?.map(p => p?.id);
                onSelectAll(employerIds);
              }}
              variant="outline"
              iconName="Building2"
            >
              Select All Employers
            </Button>
          </div>
        </div>

        {/* Bulk Actions */}
        <div className="bg-card rounded-2xl p-6 border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4">Bulk Actions</h3>
          <div className="flex gap-3">
            <Button
              onClick={handleBulkApprove}
              loading={isProcessing}
              disabled={selectedProfiles?.length === 0}
              iconName="CheckCircle"
              className="flex-1 bg-success hover:bg-success/90 text-white"
            >
              Approve {selectedProfiles?.length} Selected
            </Button>
            <Button
              onClick={() => setShowBulkRejectModal(true)}
              disabled={selectedProfiles?.length === 0}
              iconName="XCircle"
              className="flex-1 bg-error hover:bg-error/90 text-white"
            >
              Reject {selectedProfiles?.length} Selected
            </Button>
          </div>
        </div>

        {/* Selected Profiles Preview */}
        {selectedProfiles?.length > 0 && (
          <div className="bg-card rounded-2xl p-6 border border-border">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Selected Profiles ({selectedProfiles?.length})
            </h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {selectedProfilesData?.map((profile) => (
                <div
                  key={profile?.id}
                  className="flex items-center justify-between p-3 bg-background rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name="User" size={20} className="text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{profile?.fullName}</p>
                      <p className="text-sm text-muted-foreground">{profile?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      profile?.role === 'candidate' ?'bg-primary/10 text-primary' :'bg-secondary/10 text-secondary'
                    }`}>
                      {profile?.role}
                    </span>
                    {profile?.companyName && (
                      <span className="text-xs text-muted-foreground">{profile?.companyName}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Warning */}
        <div className="bg-warning/10 border border-warning/20 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
            <div>
              <p className="font-medium text-warning mb-1">Bulk Action Warning</p>
              <p className="text-sm text-muted-foreground">
                Bulk actions cannot be undone. Please review your selections carefully before proceeding.
                All selected profiles will be processed with the same decision and reason.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Reject Modal */}
      {showBulkRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl p-6 max-w-md w-full elevation-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center">
                <Icon name="XCircle" size={20} className="text-error" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Bulk Reject Profiles</h3>
                <p className="text-sm text-muted-foreground">
                  Rejecting {selectedProfiles?.length} profiles
                </p>
              </div>
            </div>

            <textarea
              value={bulkRejectReason}
              onChange={(e) => setBulkRejectReason(e?.target?.value)}
              placeholder="Enter rejection reason (will apply to all selected profiles)..."
              className="w-full h-32 px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
            />

            <div className="flex gap-3 mt-4">
              <Button
                onClick={() => {
                  setShowBulkRejectModal(false);
                  setBulkRejectReason('');
                }}
                variant="outline"
                fullWidth
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkReject}
                loading={isProcessing}
                fullWidth
                className="bg-error hover:bg-error/90 text-white"
              >
                Confirm Bulk Rejection
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BulkApprovalPanel;